import {makeStyles} from "@mui/styles";
import React, {useState} from "react";
import {Point} from "../../../../../common/diagrameditor/util/GeometryUtils";
import {ClickAwayListener, ListItemText, MenuItem, MenuList, Paper} from "@mui/material";
import {MAIN_PAGE_OVERLAY_ZINDEX} from "../../../../MainPage";
import clsx from "clsx";
import {NewConnectionDefinition} from "../../../../../common/diagrameditor/manager/ConnectionCreateManager";
import {forkJoin} from "rxjs";
import Api from "../../../../../common/Api";
import HiddenRelationshipReuseConfirmDialog from "./HiddenRelationshipReuseConfirmDialog";
import {
    ArchiMateAccess,
    ArchiMateAccessType,
    ArchimateRelationship,
    ArchimateRelationshipType
} from "../../../../../common/archimate/ArchimateRelationship";
import {ObjectType} from "../../../../../common/apis/editor/ObjectType";
import {RelationshipDto} from "../../../../../common/apis/relationship/RelationshipDto";
import {DiagramTranslationKey} from "../DiagramTranslationKey";
import {_transl} from "../../../../../store/localization/TranslMessasge";

const DIALOG_ID = "_diagram-editor-page-connection-type-dialog__";

const useStyles = makeStyles(() => ({
    dialog: {
        position: "fixed",
        zIndex: MAIN_PAGE_OVERLAY_ZINDEX + 1000,
    },
    menuItem: {
        paddingTop: 0,
        paddingBottom: 0,
    },
    menuItemText: {
        "& .MuiTypography-root": {
            fontSize: ".82em",
        }
    },
    subMenuItemText: {
        paddingLeft: 20,
    },
}));

interface ConnectionTypeSelectionDialogProps {
    allowedRelationshipTypes: ArchimateRelationship[];
    hiddenRelationships: RelationshipDto[];
    eventPoint: Point;
    onSelect: (definition: NewConnectionDefinition, event: any) => void;
    onCancel: (event: any) => void;
}

enum DialogType {
    CONNECTION_TYPE_SELECTION,
    HIDDEN_RELATIONSHIP_REUSE_CONFIRMATION
}

interface SelectionState {
    dialogType: DialogType;
    reusedRelationship?: RelationshipDto;
    selectedRelationshipType?: ArchimateRelationshipType;
    selectedDirected?: boolean;
    selectedAccessType?: ArchiMateAccessType;
    event?: any;
}

export default function ConnectionTypeSelectionDialog(props: ConnectionTypeSelectionDialogProps) {
    const {allowedRelationshipTypes, hiddenRelationships, eventPoint, onSelect, onCancel} = props;
    const classes = useStyles();

    const [state, setState] = useState<SelectionState>({
        dialogType: DialogType.CONNECTION_TYPE_SELECTION,
    });

    const onAccessTypeSelected = (accessType: ArchiMateAccessType, event: any) => {
        const relationshipType = ArchimateRelationship[ArchimateRelationshipType.ACCESS];
        const hidden = hiddenRelationships
            .filter(relationship => (relationshipType.standardNames.indexOf(relationship.type) !== -1) && (relationship.accessType === accessType));
        onSelected(relationshipType.relationshipType, accessType, undefined, hidden, event);
    };

    const onAssociationSelected = (directed: boolean, event: any) => {
        const relationshipType = ArchimateRelationship[ArchimateRelationshipType.ASSOCIATION];
        const hidden = hiddenRelationships
            .filter(relationship => (relationshipType.standardNames.indexOf(relationship.type) !== -1) && (relationship.associationDirected === directed));
        onSelected(relationshipType.relationshipType, undefined, directed, hidden, event);
    };

    const onOtherSelected = (type: ArchimateRelationshipType, event: any) => {
        const relationshipType = ArchimateRelationship[type];
        const hidden = hiddenRelationships
            .filter(relationship => relationshipType.standardNames.indexOf(relationship.type) !== -1);
        onSelected(type, undefined, undefined, hidden, event);
    };

    const onSelected = (type: ArchimateRelationshipType, accessType: ArchiMateAccessType | undefined,
        directed: boolean | undefined, hidden: RelationshipDto[], event: any) => {
        if (hidden.length > 0) {
            setState({
                ...state,
                dialogType: DialogType.HIDDEN_RELATIONSHIP_REUSE_CONFIRMATION,
                reusedRelationship: hidden[0],
                selectedRelationshipType: type,
                selectedAccessType: accessType,
                selectedDirected: directed,
                event: event,
            });
        } else {
            setState({
                ...state,
                selectedRelationshipType: type,
                selectedAccessType: accessType,
                selectedDirected: directed,
                event: event,
            });
            fetchIdsAndCreate(false, type, accessType, directed, event);
        }
    };

    const fetchIdsAndCreate = (reuseHiddenRelationship: boolean, selectedRelationshipType?: ArchimateRelationshipType,
        selectedAccessType?: ArchiMateAccessType, selectedDirected?: boolean, event?: any) => {
        forkJoin([
            Api.editor.generateIdentifier(ObjectType.CONNECTION),
            Api.editor.generateIdentifier(ObjectType.RELATIONSHIP)
        ]).subscribe({
            next: (result) => {
                if (reuseHiddenRelationship) {
                    createConnectionWithReusedRelationship(result[0].response, event);
                } else {
                    createConnection(result[0].response, result[1].response, selectedRelationshipType, selectedAccessType, selectedDirected, event);
                }
            },
            error: () => onCancel(event),
        });
    };

    const createConnectionWithReusedRelationship = (connectionId: any, event?: any) => {
        const definition: NewConnectionDefinition = {
            existingRelationship: {
                identifier: state.reusedRelationship?.identifier as string
            },
            connectionIdentifier: connectionId,
        };
        onSelect(definition, event ?? state.event);
    };

    const createConnection = (connectionId: string, relationshipId: string, selectedRelationshipType?: ArchimateRelationshipType,
        selectedAccessType?: ArchiMateAccessType, selectedDirected?: boolean, event?: any) => {
        const definition: NewConnectionDefinition = {
            newRelationship: {
                identifier: relationshipId,
                type: selectedRelationshipType as ArchimateRelationshipType,
                accessType: selectedAccessType,
                directed: selectedDirected,
            },
            connectionIdentifier: connectionId,
        };
        onSelect(definition, event ?? state.event);
    };

    const handleConfirmReuse = (reuse: boolean) => {
        if (reuse) {
            fetchIdsAndCreate(true);
        } else {
            fetchIdsAndCreate(false, state.selectedRelationshipType, state.selectedAccessType, state.selectedDirected, state.event);
        }
    };

    return (
        <>
            {state.dialogType === DialogType.CONNECTION_TYPE_SELECTION && (
                <div id={DIALOG_ID} className={classes.dialog} style={{top: eventPoint.y, left: eventPoint.x}}>
                    <ClickAwayListener onClickAway={(event) => onCancel(event)}>
                        <Paper>
                            <MenuList dense={true}>
                                {allowedRelationshipTypes.map(relationShipType => {
                                    if (relationShipType.relationshipType === ArchimateRelationshipType.ACCESS) {
                                        return (
                                            <React.Fragment key={relationShipType.relationshipType}>
                                                <MenuItem className={classes.menuItem} dense={true} disabled={true}>
                                                    <ListItemText className={classes.menuItemText}>
                                                        {relationShipType.visibleName}
                                                    </ListItemText>
                                                </MenuItem>
                                                {ArchiMateAccess.values().map(accessType => (
                                                    <MenuItem
                                                        key={relationShipType.relationshipType + accessType.accessType}
                                                        className={classes.menuItem}
                                                        dense={true}
                                                        onClick={event => onAccessTypeSelected(accessType.accessType, event)}
                                                    >
                                                        <ListItemText className={clsx(classes.menuItemText, classes.subMenuItemText)}>
                                                            {relationShipType.visibleName} typu {accessType.accessType}
                                                        </ListItemText>
                                                    </MenuItem>
                                                ))}
                                            </React.Fragment>
                                        );
                                    } else if (relationShipType.relationshipType === ArchimateRelationshipType.ASSOCIATION) {
                                        return (
                                            <React.Fragment key={relationShipType.relationshipType}>
                                                <MenuItem className={classes.menuItem} dense={true} disabled={true}>
                                                    <ListItemText className={classes.menuItemText}>
                                                        {relationShipType.visibleName}
                                                    </ListItemText>
                                                </MenuItem>
                                                <MenuItem
                                                    key={relationShipType.relationshipType + "-directed"}
                                                    className={classes.menuItem}
                                                    dense={true}
                                                    onClick={event => onAssociationSelected(true, event)}
                                                >
                                                    <ListItemText className={clsx(classes.menuItemText, classes.subMenuItemText)}>
                                                        {relationShipType.visibleName + " " + _transl(DiagramTranslationKey.DIAGRAM_EDITOR_PAGE_RELATION_TYPE_DIRECTED)}
                                                    </ListItemText>
                                                </MenuItem>
                                                <MenuItem
                                                    key={relationShipType.relationshipType + "-undirected"}
                                                    className={classes.menuItem}
                                                    dense={true}
                                                    onClick={event => onAssociationSelected(false, event)}
                                                >
                                                    <ListItemText className={clsx(classes.menuItemText, classes.subMenuItemText)}>
                                                        {relationShipType.visibleName + " " + _transl(DiagramTranslationKey.DIAGRAM_EDITOR_PAGE_RELATION_TYPE_NON_UNDIRECTED)}
                                                    </ListItemText>
                                                </MenuItem>
                                            </React.Fragment>
                                        );
                                    } else {
                                        return (
                                            <MenuItem
                                                key={relationShipType.relationshipType}
                                                className={classes.menuItem}
                                                dense={true}
                                                onClick={event => onOtherSelected(relationShipType.relationshipType, event)}
                                            >
                                                <ListItemText className={classes.menuItemText}>
                                                    {relationShipType.visibleName}
                                                </ListItemText>
                                            </MenuItem>
                                        );
                                    }
                                })}
                            </MenuList>
                        </Paper>
                    </ClickAwayListener>
                </div>
            )}

            {state.dialogType === DialogType.HIDDEN_RELATIONSHIP_REUSE_CONFIRMATION && (
                <HiddenRelationshipReuseConfirmDialog
                    hiddenRelationship={state.reusedRelationship as RelationshipDto}
                    onConfirm={handleConfirmReuse}
                    onCancel={(event) => onCancel(event)}
                />
            )}
        </>
    );
}